import * as React from "react";
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";

// Define textarea state variants
export type TextareaState =
  | "default"
  | "error"
  | "warning"
  | "success"
  | "info";

// Extended textarea props with state support
export interface TextareaProps extends React.ComponentProps<"textarea"> {
  state?: TextareaState;
  helperText?: string;
  showIcon?: boolean;
  characterCount?: boolean;
  maxLength?: number;
}

// State-specific styling configuration
const stateStyles: Record<TextareaState, string> = {
  default: "border-input focus-visible:ring-ring",
  error:
    "border-error-border bg-error-light text-error-foreground placeholder:text-error-foreground/50 focus-visible:ring-error-border",
  warning:
    "border-warning-border bg-warning-light text-warning-foreground placeholder:text-warning-foreground/50 focus-visible:ring-warning-border",
  success:
    "border-success-border bg-success-light text-success-foreground placeholder:text-success-foreground/50 focus-visible:ring-success-border",
  info: "border-info-border bg-info-light text-info-foreground placeholder:text-info-foreground/50 focus-visible:ring-info-border",
};

// State icon mapping
const stateIcons: Record<
  Exclude<TextareaState, "default">,
  React.ElementType
> = {
  error: AlertCircle,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      state = "default",
      helperText,
      showIcon = true,
      characterCount = false,
      maxLength,
      ...props
    },
    ref,
  ) => {
    const [charCount, setCharCount] = React.useState(0);
    const StateIcon = state !== "default" ? stateIcons[state] : null;
    const hasIcon = showIcon && StateIcon;

    // Track character count
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (characterCount) {
        setCharCount(e.target.value.length);
      }
      props.onChange?.(e);
    };

    return (
      <div className="w-full">
        <div className="relative">
          <textarea
            className={cn(
              // Base styles
              "flex min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-base ring-offset-background",
              "placeholder:text-muted-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "transition-colors duration-200 resize-y",
              "md:text-sm",
              // State-specific styles
              stateStyles[state],
              // Add padding for icon if present
              hasIcon && "pr-10",
              className,
            )}
            ref={ref}
            maxLength={maxLength}
            onChange={handleChange}
            {...props}
          />
          {/* State icon positioned absolutely at top right */}
          {hasIcon && StateIcon && (
            <div className="absolute right-3 top-3 pointer-events-none">
              <StateIcon
                className={cn(
                  "h-4 w-4",
                  state === "error" && "text-error",
                  state === "warning" && "text-warning",
                  state === "success" && "text-success",
                  state === "info" && "text-info",
                )}
              />
            </div>
          )}
        </div>
        {/* Helper text and character count */}
        <div className="mt-1.5 flex items-center justify-between gap-2">
          {helperText && (
            <p
              className={cn(
                "text-xs",
                state === "default" && "text-muted-foreground",
                state === "error" && "text-error-foreground",
                state === "warning" && "text-warning-foreground",
                state === "success" && "text-success-foreground",
                state === "info" && "text-info-foreground",
              )}
            >
              {helperText}
            </p>
          )}
          {characterCount && maxLength && (
            <p
              className={cn(
                "text-xs ml-auto",
                charCount > maxLength * 0.9
                  ? "text-warning"
                  : "text-muted-foreground",
                charCount >= maxLength && "text-error",
              )}
            >
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
